import React, { useCallback, useMemo, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Badge,
} from "@chakra-ui/react";
import { Formik, Form, Field, FieldProps } from "formik";
import * as Yup from "yup";
import { FiX, FiGrid, FiLock, FiEdit2, FiCheck } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { FormLabel } from "newui/components/ui/FormLabel";
import { CheckboxWithLabel } from "newui/components/ui/CheckboxWithLabel";
import { Modal } from "newui/components/ui/Modal";
import Tooltip from "components/Tooltip";
import { useHasTwoFactorAuth } from "hooks/featureFlags";
import { RbacRolePills } from "newui/features/Settings/components/RolesPermissions/RbacRolePills";
import { useUserAssignments } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import { useCompany2faSettings } from "newui/features/Settings/hooks/useCompany2faSettings";
import { isNotNullOrUndefined } from "newui/utils/objectUtil";
import type { InviteUserPayload, UserListItem } from "../../types/userInvite";
import { useInvitePreflight } from "./hooks/useInvitePreflight";
import { useInviteUserMutation } from "./hooks/useInviteUserMutation";
import { useDeleteDraftMutation } from "./hooks/useDeleteDraftMutation";
import { useResendInviteMutation } from "./hooks/useResendInviteMutation";
import { useAdminUpdateUserMutation } from "./hooks/useAdminUpdateUserMutation";
import { useAdminResetUser2faMutation } from "./hooks/useAdminResetUser2faMutation";
import { useDeactivateUserMutation } from "./hooks/useDeactivateUserMutation";
import { useReactivateUserMutation } from "./hooks/useReactivateUserMutation";
import { InviteUserConfirmModal } from "./InviteUserConfirmModal";
import { UserStatusConfirmModal } from "./UserStatusConfirmModal";
import { UserStatusBadge } from "./UserListTable/UserStatusBadge";

// A default legacy role for RBAC-v2 invites — effective access is governed by
// RBAC assignments configured after the draft is created, so the legacy role is
// vestigial. Kept because the invite endpoint still requires a role_id.
const DEFAULT_LEGACY_ROLE_ID = 3; // "user"

type UserDrawerMode = "invite" | "edit";

interface UserDrawerProps {
  isOpen: boolean;
  mode: UserDrawerMode;
  rbacEnabled?: boolean;
  // Edit mode: the user being edited.
  user?: UserListItem | null;
  onClose: () => void;
  // Opens the shared AssignRoleDrawer for role/scope management on a real user.
  // The drawer stays mounted underneath so in-progress edits survive the detour.
  onManageRoles?: (user: UserListItem) => void;
  onViewAccess?: (user: UserListItem) => void;
}

interface IdentityFormValues {
  email: string;
  email_confirmation: string;
  first_name: string;
  last_name: string;
  phone: string;
}

interface ManageFormValues {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
}

const identitySchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  email_confirmation: Yup.string()
    .email("Invalid email address")
    .required("Confirm email is required")
    .oneOf([Yup.ref("email")], "Email addresses do not match"),
  phone: Yup.string(),
});

const manageSchema = Yup.object({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  phone: Yup.string(),
});

const INITIAL_IDENTITY: IdentityFormValues = {
  first_name: "",
  last_name: "",
  email: "",
  email_confirmation: "",
  phone: "",
};

export const UserDrawer = ({
  isOpen,
  mode,
  onClose,
  user = null,
  rbacEnabled = false,
  onManageRoles,
  onViewAccess,
}: UserDrawerProps) => {
  // Invite mode: the draft (ghost) user created in step 1, configured in step 2.
  const [draftUser, setDraftUser] = useState<UserListItem | null>(null);
  const [confirmPayload, setConfirmPayload] = useState<InviteUserPayload | null>(null);
  const [requiresOverride, setRequiresOverride] = useState(false);
  // Edit mode: email is locked until the admin explicitly opens it for editing.
  const [isEmailUnlocked, setIsEmailUnlocked] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [statusConfirm, setStatusConfirm] = useState<"deactivate" | "reactivate" | null>(null);
  // The `user` prop is a snapshot from the parent list, so a status change isn't
  // reflected until the list refetches and the drawer re-opens. Track the new
  // status locally to update the display immediately.
  const [statusOverride, setStatusOverride] = useState<UserListItem["status"] | null>(null);

  const preflight = useInvitePreflight();
  const invite = useInviteUserMutation();
  const deleteDraft = useDeleteDraftMutation();
  const sendInvite = useResendInviteMutation();
  const update = useAdminUpdateUserMutation();
  const resetTwoFactor = useAdminResetUser2faMutation();
  const deactivate = useDeactivateUserMutation();
  const reactivate = useReactivateUserMutation();
  const { data: company2FA, isLoading: is2FaLoading } = useCompany2faSettings();
  const is2faEnabled = useHasTwoFactorAuth();

  const isInviteMode = mode === "invite";
  // The user currently under management: the edited user, or the created draft.
  const managedUser = mode === "edit" ? user : draftUser;
  const isConfigurePhase = isNotNullOrUndefined(managedUser);
  const isUnsentDraft = mode === "invite" && isNotNullOrUndefined(draftUser);
  // Prefer a just-applied local status change over the stale parent snapshot.
  const displayStatus = statusOverride ?? managedUser?.status ?? null;

  // Live RBAC roles for the managed user (edit target or draft). Refreshes when
  // the AssignRoleDrawer mutates assignments (shared query key invalidation).
  const { users: assignmentUsers } = useUserAssignments({ enabled: rbacEnabled && isOpen });
  const managedAssignment = useMemo(() => {
    if (!rbacEnabled || managedUser === null) {
      return null;
    }
    return assignmentUsers.find((a) => a.id === managedUser.id) ?? null;
  }, [rbacEnabled, managedUser, assignmentUsers]);

  const resetLocalState = useCallback(() => {
    setDraftUser(null);
    setConfirmPayload(null);
    setRequiresOverride(false);
    setIsEmailUnlocked(false);
    setStatusConfirm(null);
    setStatusOverride(null);
    preflight.clearResult();
    invite.reset();
  }, [preflight, invite]);

  const handleClose = useCallback(() => {
    // Abandoning an unsent draft discards it (and any staged RBAC assignments).
    // Guard on the id itself: a draft without one cannot be addressed server-side,
    // and DELETE users/undefined/invite/draft 500s on the bigint cast (PRO-717).
    if (isUnsentDraft && draftUser !== null && isNotNullOrUndefined(draftUser.id)) {
      deleteDraft.mutate(draftUser.id);
    }
    resetLocalState();
    onClose();
  }, [isUnsentDraft, draftUser, deleteDraft, resetLocalState, onClose]);

  // ---- Invite: step 1 (identity) → create the draft ----
  const createDraft = (values: IdentityFormValues, overrideReason?: string) => {
    const payload: InviteUserPayload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      email_confirmation: values.email_confirmation,
      phone: values.phone || null,
      role_id: DEFAULT_LEGACY_ROLE_ID,
      draft: true,
      ...(overrideReason ? { domain_override_reason: overrideReason } : {}),
    };
    invite.mutate(payload, {
      onSuccess: (created) => {
        setConfirmPayload(null);
        setDraftUser(created);
      },
    });
  };

  const handleIdentitySubmit = (values: IdentityFormValues) => {
    // Domain not authorised → collect an override reason before creating.
    if (preflight.result?.requires_override_reason) {
      setRequiresOverride(true);
      setConfirmPayload({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        email_confirmation: values.email_confirmation,
        phone: values.phone || null,
        role_id: DEFAULT_LEGACY_ROLE_ID,
        draft: true,
      });
      return;
    }
    createDraft(values);
  };

  const handleOverrideConfirm = (overrideReason?: string) => {
    if (!confirmPayload) {
      return;
    }
    createDraft(
      {
        first_name: confirmPayload.first_name,
        last_name: confirmPayload.last_name,
        email: confirmPayload.email,
        email_confirmation: confirmPayload.email_confirmation,
        phone: confirmPayload.phone ?? "",
      },
      overrideReason,
    );
  };

  // ---- Invite: step 2 (configure) → send the invite ----
  const handleSendInvite = () => {
    if (!draftUser) {
      return;
    }
    sendInvite.mutate(
      { userId: draftUser.id },
      {
        onSuccess: () => {
          // Clear the draft so handleClose does not delete it, then close.
          setDraftUser(null);
          resetLocalState();
          onClose();
        },
      },
    );
  };

  // ---- Edit: save profile (incl. email when unlocked) ----
  const handleEditSubmit = (values: ManageFormValues) => {
    if (!user) {
      return;
    }
    update.mutate(
      {
        userId: user.id,
        payload: {
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone || null,
          ...(isEmailUnlocked && values.email !== user.email ? { email: values.email } : {}),
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  // ---- Status toggle (edit, active ⇄ deactivated) ----
  const handleStatusConfirm = () => {
    if (!user) {
      return;
    }
    if (statusConfirm === "deactivate") {
      deactivate.mutate(user.id, {
        onSuccess: () => setStatusOverride("deactivated"),
        onSettled: () => setStatusConfirm(null),
      });
    } else if (statusConfirm === "reactivate") {
      reactivate.mutate(user.id, {
        // Reflect the server's resulting status: a registered user becomes
        // active; a never-registered user is re-invited (invited).
        onSuccess: (updated) => setStatusOverride(updated?.status ?? "active"),
        onSettled: () => setStatusConfirm(null),
      });
    }
  };

  const handleConfirmReset = () => {
    if (!user) {
      return;
    }
    resetTwoFactor.mutate(user.id, {
      onSuccess: () => {
        setResetUserId(user.id);
        setIsResetConfirmOpen(false);
      },
    });
  };

  const existingUser = isNotNullOrUndefined(preflight.result?.existing_user);
  const twoFactorEnabled = !!user?.two_factor_enabled && resetUserId !== user?.id;

  const headerTitle =
    mode === "edit" ? "Edit User" : isConfigurePhase ? "Configure & invite" : "Invite User";

  const renderRolesSection = (targetUser: UserListItem) => {
    if (!rbacEnabled) {
      return null;
    }
    return (
      <div>
        <FormLabel label="Roles & access" />
        <div
          className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
          data-testid="user-drawer-rbac-roles"
        >
          <div className="pt-0.5">
            <RbacRolePills assignment={managedAssignment} />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onViewAccess && (
              <Button
                type="button"
                btntype="plain-border"
                size="xs"
                onClick={() => onViewAccess(targetUser)}
                className="flex items-center gap-1.5"
                data-testid="user-drawer-view-access"
              >
                <FiGrid size={14} />
                View access
              </Button>
            )}
            {onManageRoles && (
              <Button
                type="button"
                btntype="plain-border"
                size="xs"
                onClick={() => onManageRoles(targetUser)}
                data-testid="user-drawer-manage-roles"
              >
                Manage roles
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Drawer placement="right" isOpen={isOpen} onClose={handleClose} size="md">
        <DrawerOverlay onClick={handleClose} />
        <DrawerContent data-testid="user-drawer-panel">
          <DrawerHeader borderBottomWidth="1px" className="flex items-center justify-between">
            <span className="text-base font-semibold text-gray-900">{headerTitle}</span>
            <button
              type="button"
              onClick={handleClose}
              className="rounded p-1 text-gray-500 hover:text-gray-700"
              aria-label="Close drawer"
              data-testid="user-drawer-close"
            >
              <FiX size={18} />
            </button>
          </DrawerHeader>

          {/* ---- Invite identity step ---- */}
          {mode === "invite" && !isConfigurePhase && (
            <DrawerBody className="py-6" data-testid="user-drawer-identity">
              <Formik
                initialValues={INITIAL_IDENTITY}
                validationSchema={identitySchema}
                onSubmit={handleIdentitySubmit}
              >
                {({ setFieldValue }) => (
                  <Form className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormikInputWithLabel name="first_name" label="First name" placeholder="First name" required />
                      <FormikInputWithLabel name="last_name" label="Last name" placeholder="Last name" required />
                    </div>

                    <Field name="email">
                      {({ field, meta }: FieldProps) => (
                        <FormikInputWithLabel
                          name="email"
                          label="Email address"
                          placeholder="name@company.com"
                          type="email"
                          required
                          testId="invite-email-input"
                          onBlur={(e) => {
                            field.onBlur(e);
                            const email = e.target.value.trim();
                            if (email && !meta.error) {
                              preflight.checkEmail(email);
                            }
                          }}
                          onChange={(e) => {
                            setFieldValue("email", e.target.value);
                            preflight.clearResult();
                          }}
                        />
                      )}
                    </Field>

                    {existingUser && (
                      <div className="rounded-md border border-blue-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                        A user with this email already exists with status:{" "}
                        <strong>{preflight.result?.existing_user?.status}</strong>.
                      </div>
                    )}

                    <FormikInputWithLabel
                      name="email_confirmation"
                      label="Confirm email address"
                      placeholder="name@company.com"
                      type="email"
                      required
                    />

                    <FormikInputWithLabel
                      name="phone"
                      label="Phone number"
                      placeholder="+61 4XX XXX XXX"
                      type="tel"
                      testId="invite-phone-input"
                    />

                    {preflight.result?.requires_override_reason && (
                      <div
                        className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                        data-testid="domain-override-warning"
                      >
                        <FiLock className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                          This email domain is not on your company&apos;s authorised list. You will
                          need to provide a reason for this exception.
                        </span>
                      </div>
                    )}

                    <DrawerFooter borderTopWidth="1px" className="flex justify-end gap-3 px-0 pt-4">
                      <Button btntype="base" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Tooltip
                        label={existingUser && "A user with this email address already exists!"}
                        shouldWrapChildren
                      >
                        <Button
                          type="submit"
                          data-testid="invite-create-draft-button"
                          disabled={existingUser || invite.isPending}
                        >
                          {invite.isPending ? "Creating…" : "Create & configure"}
                        </Button>
                      </Tooltip>
                    </DrawerFooter>
                  </Form>
                )}
              </Formik>
            </DrawerBody>
          )}

          {/* ---- Configure (draft) OR Edit — shared management view ---- */}
          {isConfigurePhase && managedUser && (
            <Formik
              enableReinitialize
              initialValues={{
                first_name: managedUser.first_name,
                last_name: managedUser.last_name,
                email: managedUser.email,
                phone: managedUser.phone ?? "",
              }}
              validationSchema={manageSchema}
              onSubmit={handleEditSubmit}
            >
              {({ isSubmitting, resetForm }) => (
                <Form
                  className="flex flex-col h-full"
                  data-testid={isInviteMode ? "invite-user-form" : "edit-user-form"}
                >
                  {/*
                    * `min-h-0` is the fix, not decoration: a flex child defaults to
                    * min-height:auto, so this body grew to fit its content instead of
                    * scrolling, pushing Save and Cancel below the viewport while the
                    * wheel scrolled the page behind the drawer. With it the body takes
                    * the leftover height and scrolls itself, so the footer is always
                    * reachable no matter how much the content grows.
                    */}
                  <DrawerBody
                    className="min-h-0 flex-1 space-y-4 overflow-y-auto py-6"
                    data-testid="user-drawer-manage"
                  >
                    {/* Email — locked in edit until unlocked; read-only for a draft */}
                    {mode === "edit" && isEmailUnlocked ? (
                      <FormikInputWithLabel
                        name="email"
                        label="Email address"
                        type="email"
                        required
                        testId="edit-user-email-input"
                      />
                    ) : (
                      <div>
                        <FormLabel label="Email address" />
                        <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                          <span className="text-sm text-gray-500" data-testid="user-drawer-email-readonly">
                            {managedUser.email}
                          </span>
                          {mode === "edit" && (
                            <button
                              type="button"
                              onClick={() => setIsEmailUnlocked(true)}
                              className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900"
                              data-testid="edit-user-email-unlock"
                            >
                              <FiEdit2 size={12} />
                              Change
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {renderRolesSection(managedUser)}

                    {/* Status — edit only. Draft is implicitly "invited". */}
                    {mode === "edit" && (
                      <div>
                        <FormLabel label="Status" />
                        {displayStatus === "invited" ? (
                          <div data-testid="user-drawer-status-invited">
                            <UserStatusBadge status="invited" />
                          </div>
                        ) : (
                          <div data-testid="user-drawer-status-toggle">
                            {/* `w-fit` keeps the hit area on the checkbox and the
                                status badge only. CheckboxWithLabel renders a
                                <label> with `flex`, which is block-level and so
                                stretches the full drawer width — that made the
                                empty space to the right of the badge toggle the
                                user's active state on a stray click. */}
                            <CheckboxWithLabel
                              checked={displayStatus === "active"}
                              onChange={() =>
                                // Intent is derived from the current status (active ->
                                // deactivate, otherwise reactivate) rather than the event value.
                                setStatusConfirm(
                                  displayStatus === "active" ? "deactivate" : "reactivate",
                                )
                              }
                              className="!mb-0 w-fit"
                            >
                              <UserStatusBadge status={displayStatus} />
                            </CheckboxWithLabel>
                          </div>
                        )}
                      </div>
                    )}

                    <FormikInputWithLabel
                      name="first_name"
                      label="First name"
                      placeholder="First name"
                      required
                      testId="user-drawer-first-name"
                    />
                    <FormikInputWithLabel
                      name="last_name"
                      label="Last name"
                      placeholder="Last name"
                      required
                      testId="user-drawer-last-name"
                    />
                    <FormikInputWithLabel
                      name="phone"
                      label="Phone number"
                      placeholder="+61 4XX XXX XXX"
                      type="tel"
                      testId="user-drawer-phone"
                    />

                    {mode === "edit" && !is2FaLoading && is2faEnabled && (
                      <div className="mt-10">
                        <Badge
                          className="mb-2"
                          background={twoFactorEnabled ? "green.500" : "red.400"}
                          color="white"
                        >
                          {!twoFactorEnabled && "Not "} Enrolled in 2FA
                        </Badge>
                        {twoFactorEnabled && (
                          <Button
                            type="button"
                            btntype="secondary"
                            onClick={() => setIsResetConfirmOpen(true)}
                            data-testid="reset-2fa-btn"
                          >
                            Reset User 2FA
                          </Button>
                        )}
                      </div>
                    )}
                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px">
                    <div className="flex w-full justify-end gap-3">
                      <Button
                        type="button"
                        btntype="base"
                        onClick={() => {
                          resetForm();
                          handleClose();
                        }}
                        data-testid="user-drawer-cancel"
                      >
                        Cancel
                      </Button>
                      {mode === "invite" ? (
                        <Button
                          type="button"
                          onClick={handleSendInvite}
                          disabled={sendInvite.isPending}
                          data-testid="user-drawer-send-invite"
                        >
                          {sendInvite.isPending ? "Sending…" : "Send invite"}
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          disabled={update.isPending || isSubmitting}
                          data-testid="user-drawer-save"
                        >
                          {update.isPending ? "Saving…" : "Save changes"}
                        </Button>
                      )}
                    </div>
                  </DrawerFooter>
                </Form>
              )}
            </Formik>
          )}
        </DrawerContent>
      </Drawer>

      {confirmPayload && (
        <InviteUserConfirmModal
          isOpen
          email={confirmPayload.email}
          requiresOverrideReason={requiresOverride}
          isLoading={invite.isPending}
          onConfirm={handleOverrideConfirm}
          onClose={() => setConfirmPayload(null)}
        />
      )}

      {statusConfirm && user && (
        <UserStatusConfirmModal
          isOpen
          action={statusConfirm}
          firstName={user.first_name}
          isLoading={deactivate.isPending || reactivate.isPending}
          onConfirm={handleStatusConfirm}
          onClose={() => setStatusConfirm(null)}
        />
      )}

      <Modal
        title="Reset User Two Factor Authentication?"
        modalType="alert"
        iconType="warning"
        isOpen={isResetConfirmOpen}
        onClose={() => setIsResetConfirmOpen(false)}
        closeOnEsc
        closeOnOverlayClick
        primaryButton={{
          text: "Reset Two Factor Authentication",
          btntype: "danger",
          onClick: handleConfirmReset,
          isLoading: resetTwoFactor.isPending,
          disabled: resetTwoFactor.isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          btntype: "secondary",
          onClick: () => setIsResetConfirmOpen(false),
          disabled: resetTwoFactor.isPending,
        }}
      >
        <div className="text-center text-md space-y-4 mb-4 text-gray-600">
          <p>
            Are you sure you want to reset two-factor authentication for{" "}
            <strong>
              {user?.first_name} {user?.last_name}
            </strong>
            ?
          </p>
          <p>
            {company2FA?.is_2fa_enforced
              ? "They will be required to set up 2FA again on their next login."
              : "This will disable 2FA for this user and they will have to set it up again."}
          </p>
        </div>
      </Modal>
    </>
  );
};
